import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useExploreImage } from "../ExploreImageContext";
import type {
  ExtractedColor,
  ResultsSortOption,
  ResultsViewMode,
} from "../types";

interface ResultsContextValue {
  activeRegionIndex: number;
  setActiveRegionIndex: (index: number) => void;
  selectedColor: ExtractedColor | null;
  selectedColorIndex: number;
  setSelectedColorIndex: (index: number) => void;
  clearSelectedColor: () => void;
  sortOption: ResultsSortOption;
  setSortOption: (option: ResultsSortOption) => void;
  viewMode: ResultsViewMode;
  setViewMode: (mode: ResultsViewMode) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const ResultsContext = createContext<ResultsContextValue | null>(null);

/**
 * View-state provider for the results screen.
 *
 * Owns the transient UI state that sits on top of `ExploreImageContext`'s
 * extract results: which region tab is active, which palette swatch is
 * selected, and the grid's sort/view/search controls. Resetting the selected
 * color to the first swatch whenever the active region changes is handled
 * here so consumers don't have to coordinate that themselves.
 *
 * Must be mounted inside an `ExploreImageProvider` — it reads
 * `state.extractResults` to derive the currently selected color.
 */
export const ResultsProvider = ({ children }: { children: ReactNode }) => {
  const { state } = useExploreImage();
  const [activeRegionIndex, setActiveRegionIndex] = useState(0);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [sortOption, setSortOption] = useState<ResultsSortOption>("deltaE");
  const [viewMode, setViewMode] = useState<ResultsViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");

  const activePalette =
    state.extractResults?.regions[activeRegionIndex]?.palette;

  useEffect(() => {
    setSelectedColorIndex(0);
  }, [activeRegionIndex]);

  const selectedColor = useMemo(
    () => activePalette?.[selectedColorIndex] ?? null,
    [activePalette, selectedColorIndex],
  );

  const handleSetActiveRegion = useCallback((index: number) => {
    setActiveRegionIndex(index);
  }, []);

  // A swatch click always selects that swatch; re-clicking the selected one is
  // a no-op. Toggling off to -1 was removed (CX-3807) because it collided with
  // the discard flow's clearSelectedColor() (also -1), collapsing the whole
  // Upload & Match section without the confirmation the discard flow requires.
  const handleSetSelectedColor = useCallback((index: number) => {
    setSelectedColorIndex(index);
  }, []);

  const clearSelectedColor = useCallback(() => {
    setSelectedColorIndex(-1);
  }, []);

  const value = useMemo(
    () => ({
      activeRegionIndex,
      setActiveRegionIndex: handleSetActiveRegion,
      selectedColor,
      selectedColorIndex,
      setSelectedColorIndex: handleSetSelectedColor,
      clearSelectedColor,
      sortOption,
      setSortOption,
      viewMode,
      setViewMode,
      searchQuery,
      setSearchQuery,
    }),
    [
      activeRegionIndex,
      handleSetActiveRegion,
      selectedColor,
      selectedColorIndex,
      handleSetSelectedColor,
      clearSelectedColor,
      sortOption,
      viewMode,
      searchQuery,
    ],
  );

  return (
    <ResultsContext.Provider value={value}>{children}</ResultsContext.Provider>
  );
};

/**
 * Accessor for the results view-state. Throws if used outside a
 * `ResultsProvider`, so consumers can rely on the returned value being
 * non-null.
 */
export const useResults = () => {
  const context = useContext(ResultsContext);
  if (!context) {
    throw new Error("useResults must be used within a ResultsProvider");
  }
  return context;
};
